'use client'

import { motion } from 'framer-motion'
import WhyChooseUs from '@/components/WhyChooseUs'
import About from '@/components/About'
import { Mail, Phone, MapPin, Award, Users, Building, Globe, CheckCircle, Star, Target, Zap, Shield, Code } from 'lucide-react'
import Link from 'next/link'
import DynamicCompanyInfo from '@/components/DynamicCompanyInfo'

export default function AboutPage() {
  const stats = [
    { value: '400+', label: 'Projects Completed' },
    { value: '150+', label: 'Happy Clients' },
    { value: '40+', label: 'Team Members' },
    { value: '5.0★', label: 'Client Rating' },
    { value: '6+', label: 'Years Experience' },
    { value: '25+', label: 'Countries Served' }
  ]

  const officeLocations = [
    {
      country: 'India Office',
      phone: '+91-7428750670',
      flag: '🇮🇳',
      address: 'Bangalore, India'
    },
    {
      country: 'USA Office', 
      phone: '+1 323-908-3492',
      flag: '🇺🇸',
      address: 'San Francisco, USA'
    },
    {
      country: 'India Office',
      phone: '+91 74280 78764',
      flag: '🇮🇳', 
      address: 'Mumbai, India'
    }
  ]

  const certifications = [
    {
      name: 'ISO 9001:2015',
      description: 'Quality Management System',
      type: 'Quality Certified IT Solution Provider'
    },
    {
      name: 'ISO 27001:2013',
      description: 'Information Security Management',
      type: 'Security Certified Provider'
    },
    {
      name: 'Microsoft Partner',
      description: 'Gold Certified Partner',
      type: 'Technology Partnership'
    },
    {
      name: 'AWS Partner',
      description: 'Advanced Consulting Partner',
      type: 'Cloud Solutions Provider'
    }
  ]

  const companyValues = [
    {
      icon: <Target className="w-8 h-8" />,
      title: 'Mission Excellence',
      description: 'To deliver innovative technology solutions that transform businesses and drive sustainable growth in the digital age.'
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'Innovation Leadership',
      description: 'We stay ahead of technological trends and leverage cutting-edge solutions to solve complex business challenges.'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Quality Assurance', 
      description: 'Maintaining the highest standards of quality, security, and reliability in every project we deliver.'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Client Partnership',
      description: 'Building long-term relationships based on trust, transparency, and mutual success.'
    }
  ]

  const teamMembers = [
    {
      name: 'Rajesh Kumar',
      position: 'CEO & Founder',
      experience: '15+ years',
      expertise: 'Strategic Leadership, Digital Transformation',
      image: '👨‍💼'
    },
    {
      name: 'Priya Sharma',
      position: 'CTO & Co-Founder', 
      experience: '12+ years',
      expertise: 'Cloud Architecture, Enterprise Solutions',
      image: '👩‍💻'
    },
    {
      name: 'Michael Johnson',
      position: 'VP International Operations',
      experience: '10+ years', 
      expertise: 'Global Business Development, Client Relations',
      image: '👨‍💼'
    },
    {
      name: 'Anita Patel',
      position: 'Head of Delivery',
      experience: '8+ years',
      expertise: 'Project Management, Quality Assurance', 
      image: '👩‍💼'
    }
  ]

  const milestones = [
    { year: '2019', event: 'Company Founded', description: 'Started with a vision to transform businesses through technology' },
    { year: '2020', event: 'First International Client', description: 'Expanded operations to serve global markets' },
    { year: '2021', event: 'ISO Certification', description: 'Achieved ISO 9001:2015 and ISO 27001:2013 certifications' },
    { year: '2022', event: '100+ Projects Milestone', description: 'Successfully delivered 100+ projects across various industries' },
    { year: '2023', event: 'Cloud Excellence', description: 'Became AWS Advanced Consulting Partner' },
    { year: '2024', event: 'AI Innovation Lab', description: 'Launched dedicated AI/ML research and development center' }
  ]

  return (
    <main className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                We're the Top <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">#1 IT Company</span>
              </h1>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-8">
                Contributing towards a Smart World!
              </h2>
              <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                From websites to applications, along with the latest software technologies, 
                we can make up all kinds of successful solutions.
              </p>
            </motion.div>
          </div>

          {/* Stats Grid */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-16"
          >
            {stats.map((stat, index) => (
              <div key={index} className="text-center bg-white rounded-2xl p-6 shadow-lg">
                <div className="text-3xl font-bold text-blue-600 mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Details */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-8">Get In Touch</h2>
              
              {/* Email */}
              <div className="flex items-center mb-6 p-4 bg-blue-50 rounded-xl">
                <Mail className="w-6 h-6 text-blue-600 mr-4" />
                <div>
                  <div className="font-semibold text-gray-900">E-mail</div>
                  <DynamicCompanyInfo type="email" className="text-blue-600 hover:text-blue-700" />
                </div>
              </div>

              {/* Teams */}
              <div className="flex items-center mb-8 p-4 bg-purple-50 rounded-xl">
                <Users className="w-6 h-6 text-purple-600 mr-4" />
                <div>
                  <div className="font-semibold text-gray-900">Teams</div>
                  <DynamicCompanyInfo type="companyName">
                    {(companyInfo) => (
                      <span className="text-purple-600">{(companyInfo?.companyName || 'company').toLowerCase().replace(/\s+/g, '')}</span>
                    )}
                  </DynamicCompanyInfo>
                </div>
              </div>

              {/* Office Locations */}
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Our Global Offices</h3>
              <div className="space-y-4">
                {officeLocations.map((office, index) => (
                  <div key={index} className="flex items-center p-4 border border-gray-200 rounded-xl">
                    <div className="text-2xl mr-4">{office.flag}</div>
                    <div>
                      <div className="font-semibold text-gray-900">{office.country}</div>
                      <div className="text-blue-600">{office.phone}</div>
                      <div className="text-gray-600">{office.address}</div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Certifications */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-8">
                <div className="text-center mb-8">
                  <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Award className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">ISO 9001:2015</h3>
                  <p className="text-gray-600">Quality Certified IT Solution Provider</p>
                </div>
                
                <div className="space-y-4">
                  {certifications.map((cert, index) => (
                    <div key={index} className="bg-white p-4 rounded-xl">
                      <div className="flex items-center mb-2">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                        <span className="font-semibold text-gray-900">{cert.name}</span>
                      </div>
                      <p className="text-gray-600 text-sm ml-8">{cert.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Component */}
      <About />

      {/* Company Values */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Core Values</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide our work and define our commitment to excellence.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {companyValues.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center text-white mb-6">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Meet Our Leadership Team</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experienced leaders driving innovation and excellence in technology solutions.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-8 text-center"
              >
                <div className="text-6xl mb-4">{member.image}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{member.name}</h3>
                <p className="text-blue-600 font-semibold mb-2">{member.position}</p>
                <p className="text-sm text-gray-600 mb-3">{member.experience}</p>
                <p className="text-sm text-gray-700">{member.expertise}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Timeline */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Journey</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Key milestones in our growth and evolution as a leading technology partner.
            </p>
          </motion.div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-blue-200"></div>
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
                >
                  <div className={`w-5/12 ${index % 2 === 0 ? 'text-right pr-8' : 'text-left pl-8'}`}>
                    <div className="bg-white p-6 rounded-2xl shadow-lg">
                      <div className="text-2xl font-bold text-blue-600 mb-2">{milestone.year}</div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{milestone.event}</h3>
                      <p className="text-gray-600">{milestone.description}</p>
                    </div>
                  </div>
                  <div className="relative z-10 w-6 h-6 bg-blue-600 rounded-full border-4 border-white"></div>
                  <div className="w-5/12"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Component */}
      <WhyChooseUs />

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-purple-600">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              <DynamicCompanyInfo type="companyName">
                {(companyInfo) => (
                  `Let's discuss how ${companyInfo?.companyName || 'our company'} can help you achieve your digital transformation goals.`
                )}
              </DynamicCompanyInfo>
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link href="/contact" className="bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold hover:shadow-xl transition-all duration-300">
                Get In Touch
              </Link>
              <Link href="/services" className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-blue-600 transition-all duration-300">
                View Our Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  )
}